import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react'

import { getPlayerErrorMessage } from '../../services/account/playerApi'
import { errorClass, inputClass, linkButtonClass, secondaryButtonClass } from './styles'

interface PlayerNameEditorProps {
  /** How the player appears on the leaderboard, e.g. `Tendai #8QLRY2`. */
  label: string
  /** The current name without the tag. */
  displayName: string
  /** Saves a new name. Rejects with a `PlayerApiError` if it is not allowed. */
  onRename: (displayName: string) => Promise<unknown>
}

// The name other players see on the leaderboard. The tag after it is fixed and
// tells apart two people who pick the same name. The name is shown once, with a
// pencil to change it; the input only appears while editing.
export function PlayerNameEditor({ label, displayName, onRename }: PlayerNameEditorProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(displayName)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // A ref, not state: state is stale within one tick, so a double click could send two saves.
  const inFlight = useRef(false)

  const trimmed = draft.trim()
  const unchanged = trimmed === displayName

  const startEditing = () => {
    setDraft(displayName)
    setError(null)
    setSaved(false)
    setEditing(true)
  }

  const cancel = () => {
    if (inFlight.current) return
    setError(null)
    setEditing(false)
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (inFlight.current || unchanged || !trimmed) return
    inFlight.current = true
    setSaving(true)
    setError(null)
    try {
      await onRename(trimmed)
      setSaved(true)
      setEditing(false)
    } catch (err) {
      setError(getPlayerErrorMessage(err))
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') cancel()
  }

  if (!editing) {
    return (
      <div>
        <p className="text-xs text-white/50">Your name on the leaderboard</p>
        <div className="flex items-center gap-2">
          <p data-testid="player-label" className="text-2xl font-bold">
            {label}
          </p>
          <button
            type="button"
            aria-label="Edit name"
            title="Edit name"
            onClick={startEditing}
            className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white">
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
        </div>
        {saved && (
          <p role="status" data-testid="player-name-saved" className="text-sm text-green-400">
            Name updated
          </p>
        )}
      </div>
    )
  }

  return (
    <form onSubmit={save} onKeyDown={onKeyDown} className="space-y-3">
      <div>
        <p className="text-xs text-white/50">Your name on the leaderboard</p>
        <p className="text-sm text-white/60">
          Your tag stays <span className="font-mono">{label.slice(label.lastIndexOf('#'))}</span>
        </p>
      </div>

      <label className="block">
        <span className="sr-only">Display name</span>
        <input
          type="text"
          name="displayName"
          maxLength={20}
          autoComplete="nickname"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className={inputClass}
        />
      </label>

      {error && (
        <p role="alert" data-testid="player-name-error" className={errorClass}>
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={unchanged || !trimmed || saving} className={secondaryButtonClass}>
          {saving ? 'Saving...' : 'Save name'}
        </button>
        <button type="button" disabled={saving} onClick={cancel} className={linkButtonClass}>
          Cancel
        </button>
      </div>
    </form>
  )
}
